import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Empty,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useHireMasterLabels } from '@/hooks/useHireMasterLabels';
import pmvHireService, {
  type PmvHireAgreement,
  type PmvHireAttention,
} from '@/services/pmv-hire.service';
import {
  formatHireAgreementOption,
  resolveLabel,
} from '@/utils/pmvHireUi';

const { Text, Paragraph } = Typography;

const PmvHireAttentionPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string | undefined>('OPEN');
  const [severity, setSeverity] = useState<string | undefined>();
  const [agreementId, setAgreementId] = useState<string | undefined>();
  const [selected, setSelected] = useState<PmvHireAttention | null>(null);

  const { supplierLabelById, equipmentLabelById } = useHireMasterLabels();

  const agreementsQ = useQuery({
    queryKey: ['pmv-hire-agreements-for-attn'],
    queryFn: () => pmvHireService.listAgreements({ limit: 200 }),
    staleTime: 30_000,
  });

  const agreementById = useMemo(() => {
    const m = new Map<string, PmvHireAgreement>();
    for (const a of agreementsQ.data?.items ?? []) m.set(a.id, a);
    return m;
  }, [agreementsQ.data]);

  const q = useQuery({
    queryKey: ['pmv-hire-attention', status, severity, agreementId],
    queryFn: () =>
      pmvHireService.listAttention({
        status,
        severity,
        hire_agreement_id: agreementId,
        limit: 100,
      }),
  });

  const ack = useMutation({
    mutationFn: (id: string) => pmvHireService.ackAttention(id),
    onSuccess: () => {
      message.success('Acknowledged');
      setSelected(null);
      qc.invalidateQueries({ queryKey: ['pmv-hire-attention'] });
      qc.invalidateQueries({ queryKey: ['pmv-hire-control-tower'] });
    },
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => pmvHireService.dismissAttention(id),
    onSuccess: () => {
      message.success('Dismissed');
      setSelected(null);
      qc.invalidateQueries({ queryKey: ['pmv-hire-attention'] });
      qc.invalidateQueries({ queryKey: ['pmv-hire-control-tower'] });
    },
  });

  const items = q.data?.items ?? [];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Commercial Attention"
        subtitle="Missing rate, stale calc, invoice variance, off-hire overdue — evidence-first"
        primaryActions={
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/hire/control')}>Hire Control</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/agreements')}>Agreements</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/reconciliations')}>
              Invoice recon
            </Button>
          </Space>
        }
        extra={
          <Space wrap>
            <Select
              allowClear
              placeholder="Severity"
              style={{ width: 140 }}
              value={severity}
              onChange={setSeverity}
              options={['CRITICAL', 'WARNING', 'INFO'].map((v) => ({ value: v, label: v }))}
            />
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 140 }}
              value={status}
              onChange={setStatus}
              options={['OPEN', 'ACKNOWLEDGED', 'DISMISSED', 'RESOLVED'].map((v) => ({
                value: v,
                label: v,
              }))}
            />
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Agreement"
              style={{ width: 260 }}
              value={agreementId}
              onChange={setAgreementId}
              options={(agreementsQ.data?.items ?? []).map((a) => ({
                value: a.id,
                label: formatHireAgreementOption(a),
              }))}
            />
          </Space>
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Missing rate is never shown as 0 elsewhere. Attention codes drive follow-up, not invented money."
      />
      {!q.isLoading && items.length === 0 ? (
        <Empty description="No commercial attention for these filters" />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={q.isLoading}
          dataSource={items}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1200 }}
          onRow={(r) => ({
            onClick: () => setSelected(r),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: 'Severity',
              dataIndex: 'severity',
              width: 100,
              render: (s: string) => (
                <Tag color={s === 'CRITICAL' ? 'red' : s === 'WARNING' ? 'orange' : 'blue'}>
                  {s}
                </Tag>
              ),
            },
            { title: 'Code', dataIndex: 'attention_code', width: 180 },
            { title: 'Title', dataIndex: 'title', ellipsis: true },
            {
              title: 'Message',
              dataIndex: 'message',
              ellipsis: true,
              render: (v: string | null) => v || '—',
            },
            {
              title: 'Agreement',
              dataIndex: 'hire_agreement_id',
              width: 140,
              render: (id: string | null) => {
                if (!id) return '—';
                const a = agreementById.get(id);
                return (
                  <Button
                    type="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/asset/pmv/hire/agreements/${id}`);
                    }}
                  >
                    {a?.agreement_number || id.slice(0, 8)}
                  </Button>
                );
              },
            },
            {
              title: 'Equipment',
              dataIndex: 'pmv_profile_id',
              width: 160,
              ellipsis: true,
              render: (id: string | null) =>
                id ? (
                  <Button
                    type="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/asset/pmv/${id}?tab=hire`);
                    }}
                  >
                    {resolveLabel(equipmentLabelById, id, 'Passport')}
                  </Button>
                ) : (
                  '—'
                ),
            },
            {
              title: 'Actions',
              width: 180,
              render: (_: unknown, r: PmvHireAttention) => (
                <Space
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button size="small" onClick={() => ack.mutate(r.id)}>
                    Ack
                  </Button>
                  <Button size="small" onClick={() => dismiss.mutate(r.id)}>
                    Dismiss
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      )}

      <Drawer
        title={selected?.title || 'Attention detail'}
        open={!!selected}
        onClose={() => setSelected(null)}
        width={480}
        extra={
          selected ? (
            <Space>
              <Button size="small" onClick={() => ack.mutate(selected.id)}>
                Ack
              </Button>
              <Button size="small" danger onClick={() => dismiss.mutate(selected.id)}>
                Dismiss
              </Button>
            </Space>
          ) : null
        }
      >
        {selected ? (
          <>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Severity">
                <Tag
                  color={
                    selected.severity === 'CRITICAL'
                      ? 'red'
                      : selected.severity === 'WARNING'
                        ? 'orange'
                        : 'blue'
                  }
                >
                  {selected.severity}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Code">{selected.attention_code}</Descriptions.Item>
              <Descriptions.Item label="Status">{selected.status}</Descriptions.Item>
              <Descriptions.Item label="Agreement">
                {selected.hire_agreement_id ? (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() =>
                      navigate(`/asset/pmv/hire/agreements/${selected.hire_agreement_id}`)
                    }
                  >
                    {agreementById.get(selected.hire_agreement_id)?.agreement_number ||
                      selected.hire_agreement_id.slice(0, 8)}
                  </Button>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Equipment">
                {selected.pmv_profile_id
                  ? resolveLabel(equipmentLabelById, selected.pmv_profile_id)
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Supplier">
                {selected.hire_agreement_id
                  ? resolveLabel(
                      supplierLabelById,
                      agreementById.get(selected.hire_agreement_id)?.supplier_id
                    )
                  : '—'}
              </Descriptions.Item>
            </Descriptions>
            <Paragraph style={{ marginTop: 16 }}>
              <Text strong>Message</Text>
              <br />
              {selected.message || '—'}
            </Paragraph>
            {selected.evidence_json ? (
              <>
                <Text strong>Evidence</Text>
                <pre
                  style={{
                    fontSize: 11,
                    background: '#f5f5f5',
                    padding: 12,
                    borderRadius: 6,
                    maxHeight: 280,
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(selected.evidence_json, null, 2)}
                </pre>
              </>
            ) : null}
          </>
        ) : null}
      </Drawer>
    </WorkspaceLayout>
  );
};

export default PmvHireAttentionPage;
